import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiUser, FiClock, FiZoomIn, FiX, FiShare2, FiCheck } from 'react-icons/fi';
import api from '../services/api';
import { API_ENDPOINTS, getImageUrl } from '../utils/constants';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';

const RepairDetail = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [copied, setCopied] = useState(false);
useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    fetchArticle();
  }, [id]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await api.get(API_ENDPOINTS.REPAIR_DETAIL(id));
      setArticle(response.data);
    } catch (err) {
      setError('មិនអាចផ្ទុកអត្ថបទជួសជុលបានទេ');
      console.error('Error fetching article:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (imageUrl) => {
    setModalImage(imageUrl);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Copy link to clipboard
  const copyLinkToClipboard = async () => {
    try {
      const currentUrl = window.location.href;
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = window.location.href;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  // Share using Web Share API (mobile devices)
  const shareArticle = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.description?.replace(/<[^>]*>/g, '').substring(0, 100) || 'អត្ថបទជួសជុលទូរស័ព្ទ',
          url: window.location.href,
        });
      } catch (err) {
        console.error('Error sharing:', err);
        // Fallback to copy link
        copyLinkToClipboard();
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      copyLinkToClipboard();
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message={error} />;
  if (!article) return <ErrorAlert message="រកមិនឃើញអត្ថបទ" />;

  const images = article.images || [];
  const formattedDate = new Date(article.created_at).toLocaleDateString('km-KH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Fixed image URL construction using getImageUrl
  const mainImageUrl = images[selectedImage] 
    ? getImageUrl(images[selectedImage].filepath, 'repairs')
    : 'https://via.placeholder.com/800x500/F3F4F6/9CA3AF?text=No+Image';

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Back Button and Share Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <Link 
          to="/repairs" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors w-fit"
        >
          <FiArrowLeft size={18} />
          <span>ត្រឡប់ទៅអត្ថបទជួសជុល</span>
        </Link>

        {/* Share Buttons */}
        <div className="flex items-center gap-2">
          {/* Copy Link Button */}
          <button
            onClick={copyLinkToClipboard}
            className={`flex items-center gap-2 px-4 py-2 border rounded transition-all duration-200 ${
              copied 
                ? 'bg-green-50 border-green-500 text-green-600' 
                : 'bg-white border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-600 hover:border-red-300'
            }`}
          >
            {copied ? (
              <>
                <FiCheck size={16} />
                <span className="text-sm">ចម្លងរួចរាល់</span>
              </>
            ) : (
              <>
                <FiShare2 size={16} />
                <span className="text-sm">ចម្លងតំណ</span>
              </>
            )}
          </button>

          {/* Share Button (for mobile with native share) */}
          {navigator.share && (
            <button
              onClick={shareArticle}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              <FiShare2 size={16} />
              <span className="text-sm">ចែករំលែក</span>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8">
        {/* Title - Full Width */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            {article.title}
          </h1>

          {/* Metadata */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6 pb-4 border-b border-gray-200">
            {article.author && (
              <div className="flex items-center gap-1">
                <FiUser className="w-4 h-4" />
                <span>{article.author}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <FiClock className="w-4 h-4" />
              <span>{formattedDate}</span>
            </div>
          </div>
        </div>

        {/* Main Image with Click to Zoom */}
        {images.length > 0 && (
          <div>
            <div 
              className="bg-gray-100 border border-gray-200 overflow-hidden relative group cursor-pointer"
              onClick={() => openModal(mainImageUrl)}
            >
              <img
                src={mainImageUrl}
                alt={article.title}
                className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://via.placeholder.com/800x500/F3F4F6/9CA3AF?text=No+Image";
                }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-white/90 p-2 rounded">
                  <FiZoomIn size={24} className="text-gray-700" />
                </div>
              </div>
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`border-2 overflow-hidden transition-all duration-200 ${
                      selectedImage === index ? 'border-red-600' : 'border-transparent hover:border-red-300'
                    }`}
                  >
                    <img
                      src={getImageUrl(image.filepath, 'repairs')}
                      alt={`${article.title} - ${index + 1}`}
                      className="w-full h-auto object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/150x150/F3F4F6/9CA3AF?text=Error";
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Description - Below Image */}
        <div>
          <div 
            className="text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.description }}
          />
        </div>
      </div>

      {/* Full Screen Image Modal */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black z-[100] flex items-center justify-center"
          onClick={closeModal}
        >
          {/* Close Button */}
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 z-20 text-white hover:text-red-500 transition-colors p-2"
          >
            <FiX size={32} />
          </button>

          {/* Main Image */}
          <div 
            className="w-full h-full flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={modalImage}
              alt="Full view"
              className="max-w-full max-h-full object-contain animate-scaleIn"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/800x500/F3F4F6/9CA3AF?text=No+Image";
              }}
            />
          </div>

          {/* Image Counter */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 bg-black/70 text-white px-3 py-1 text-sm rounded">
              {selectedImage + 1} / {images.length}
            </div>
          )}
        </div>
      )}

      {/* Toast Notification for Copy Success */}
      {copied && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 animate-fadeInUp">
          <div className="bg-green-500 text-white px-4 py-2 rounded shadow-lg flex items-center gap-2">
            <FiCheck size={16} />
            <span>តំណត្រូវបានចម្លងទៅក្ដារតម្បៀតខ្ទាស់</span>
          </div>
        </div>
      )}

      {/* Add custom styles for description and animations */}
      <style>{`
        .text-gray-700 {
          line-height: 1.8;
        }
        .text-gray-700 p {
          margin-bottom: 1rem;
        }
        .text-gray-700 h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
          margin: 1.5rem 0 1rem;
        }
        .text-gray-700 h3 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1f2937;
          margin: 1.25rem 0 0.75rem;
        }
        .text-gray-700 ul, .text-gray-700 ol {
          margin: 1rem 0;
          padding-left: 1.5rem;
        }
        .text-gray-700 li {
          margin-bottom: 0.5rem;
        }
        .text-gray-700 strong {
          color: #ef4444;
          font-weight: 600;
        }
        .text-gray-700 a {
          color: #ef4444;
          text-decoration: underline;
        }
        .text-gray-700 a:hover {
          color: #dc2626;
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.2s ease-out;
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default RepairDetail;